import { PropsWithChildren, ReactElement, useCallback, useEffect } from 'react';

// project imports
import { useAppSelector, useAppDispatch } from 'hooks/useAppStore';
import { accountLogin } from 'features/account/accountSlice';
import { selectAccountInfo } from 'features/account/accountSelectors';

// types
import Loader from '../../components/loading/Loader';
import Keycloak from 'keycloak-js';
import { TOKEN_EXPIRATION, useKeycloak } from 'contexts/KeycloakContext';

// constants

// ==============================|| AUTH GUARD ||============================== //

const AuthGuard = ({ children }: PropsWithChildren): ReactElement => {
  const { keycloak } = useKeycloak();
  const dispatch = useAppDispatch();

  const accountInfo = useAppSelector(selectAccountInfo);

  const loadAndSetUserInfo = useCallback(
    (keycloakInstance: Keycloak): void => {
      keycloakInstance?.loadUserInfo().then((info: any) => {
        dispatch(
          accountLogin({
            accessToken: keycloakInstance.token,
            accountInfo: {
              session_state: keycloakInstance.tokenParsed?.session_state,
              ...info
            }
          })
        );
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (keycloak) {
      if (keycloak.authenticated) {
        loadAndSetUserInfo(keycloak);
      }

      keycloak.onTokenExpired = () => {
        keycloak.updateToken(TOKEN_EXPIRATION).then((refreshed) => {
          if (refreshed) {
            loadAndSetUserInfo(keycloak);
          }
        });
      };
    }
  }, [keycloak, loadAndSetUserInfo]);

  return (
    <>{keycloak?.authenticated && !!accountInfo ? children : <Loader />}</>
  );
};

export default AuthGuard;
